import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = '/api/ai-queue';

interface Message { role: 'user' | 'assistant'; content: string; }
interface Profile {
  id: string; name: string; queue_id: number; queue_name: string;
  knowledge_base_id?: string; system_prompt: string;
  greeting_script?: string; compliance_notice?: string; closing_script?: string;
  checklists: CheckItem[]; quick_responses: QR[]; actions: Action[];
}
interface CheckItem { id: string; type: 'opening'|'closing'; label: string; hint?: string; is_required: boolean; order_index: number; }
interface QR { id: string; label: string; response_text: string; shortcut?: string; order_index: number; }
interface Action { id: string; label: string; action_type: string; action_url?: string; action_data?: any; open_in: string; order_index: number; }

const CARD: React.CSSProperties = { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 };
const INPUT: React.CSSProperties = {
  width: '100%', background: '#f8fafc', border: '1px solid #cbd5e1',
  borderRadius: 6, padding: '8px 10px', color: '#1e293b', fontSize: 13,
  boxSizing: 'border-box' as const,
};

type PanelTab = 'chat' | 'checklist' | 'summary';

interface Props {
  user: any;
  queueId?: number;
  callId?: string;
  callerNumber?: string;
  callerName?: string;
}

export default function QueueAIPanel({ user, queueId, callId, callerNumber, callerName }: Props) {
  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const [tab, setTab] = useState<PanelTab>('chat');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [checkState, setCheckState] = useState<Record<string, boolean>>({});
  const [summary, setSummary] = useState<any>(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [noProfile, setNoProfile] = useState(false);
  const [queueList, setQueueList] = useState<{id:number;queue_name:string;display_name:string}[]>([]);
  const [selectedQueueId, setSelectedQueueId] = useState<number|undefined>(queueId);
  const chatEnd = useRef<HTMLDivElement>(null);

  // Load profile and start session
  const loadProfile = useCallback(async (qid: number) => {
    try {
      const r = await axios.get(`${API}/profiles/by-queue/${qid}`, auth);
      setProfile(r.data);
      setNoProfile(false);
      // Start session
      const sRes = await axios.post(`${API}/sessions`, {
        queue_id: qid, call_id: callId, caller_number: callerNumber, caller_name: callerName
      }, auth);
      setSessionId(sRes.data.id);
      // Add greeting if any
      if (r.data.greeting_script) {
        setMessages([{ role: 'assistant', content: `**Greeting Script:**\n${r.data.greeting_script}` }]);
      }
    } catch (e: any) {
      if (e.response?.status === 404) setNoProfile(true);
    }
  }, [callId, callerNumber, callerName]);

  useEffect(() => {
    if (selectedQueueId) { loadProfile(selectedQueueId); }
    else { axios.get('/api/queues', auth).then(r => setQueueList(r.data || [])).catch(() => {}); }
  }, [selectedQueueId, loadProfile]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (msg?: string) => {
    const text = (msg ?? input).trim();
    if (!text || !profile || thinking) return;
    const userMsg: Message = { role: 'user', content: text };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput('');
    setThinking(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        queue_id: selectedQueueId,
        knowledge_base_id: profile.knowledge_base_id,
        system_prompt: profile.system_prompt,
        messages: newMsgs.filter(m => m.role !== 'assistant' || !m.content.startsWith('**Greeting')),
        caller_number: callerNumber,
        caller_name: callerName,
      }, auth);
      setMessages(m => [...m, { role: 'assistant', content: r.data.response }]);
    } catch (e: any) {
      setMessages(m => [...m, { role: 'assistant', content: `Error: ${e.response?.data?.error || e.message}` }]);
    } finally { setThinking(false); }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleAction = (action: Action) => {
    if (action.action_type === 'copy') {
      copyToClipboard(action.action_data?.text || action.action_url || action.label);
      return;
    }
    if (action.action_url) {
      if (action.open_in === 'popup') {
        window.open(action.action_url, '_blank', 'width=900,height=700');
      } else {
        window.open(action.action_url, '_blank');
      }
    }
  };

  const generateSummary = async () => {
    if (!messages.length) return;
    setGeneratingSummary(true);
    try {
      const r = await axios.post(`${API}/summarize`, {
        session_id: sessionId,
        queue_id: selectedQueueId,
        call_id: callId,
        caller_number: callerNumber,
        caller_name: callerName,
        messages: messages.map(m => ({ role: m.role, content: m.content })),
        compliance_notice: profile?.compliance_notice,
      }, auth);
      setSummary(r.data);
      // End session
      if (sessionId) {
        await axios.patch(`${API}/sessions/${sessionId}`, {
          status: 'completed',
          opening_checklist: checkState,
          closing_checklist: checkState,
        }, auth).catch(() => {});
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Summary generation failed');
    } finally { setGeneratingSummary(false); }
  };

  const TABS: {id:PanelTab;label:string}[] = [
    { id:'chat', label:'AI Chat' },
    { id:'checklist', label:'Checklist' },
    { id:'summary', label:'Summary' },
  ];

  // Queue selector when no queueId prop
  if (!selectedQueueId) {
    return (
      <div style={{ ...CARD, height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12 }}>
        <div style={{ fontSize:32 }}>🤖</div>
        <p style={{ fontSize:13, color:'#64748b', textAlign:'center', margin:0 }}>Select a queue to activate AI assistance</p>
        <select style={{ ...INPUT, maxWidth:220 }} value={selectedQueueId||''} onChange={e => setSelectedQueueId(Number(e.target.value))}>
          <option value="">-- Select Queue --</option>
          {queueList.map(q => <option key={q.id} value={q.id}>{q.display_name||q.queue_name}</option>)}
        </select>
      </div>
    );
  }

  if (noProfile) {
    return (
      <div style={{ ...CARD, height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8 }}>
        <div style={{ fontSize:32 }}>⚙️</div>
        <p style={{ fontSize:13, color:'#64748b', textAlign:'center', margin:0 }}>No AI profile configured for this queue.</p>
        <p style={{ fontSize:11, color:'#64748b', textAlign:'center', margin:0 }}>Ask your supervisor to set one up in AI Queue Management.</p>
      </div>
    );
  }

  if (!profile) {
    return <div style={{ ...CARD, display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
      <span style={{ color:'#64748b', fontSize:13 }}>Loading AI profile...</span>
    </div>;
  }

  const openingItems = profile.checklists.filter(c => c.type === 'opening').sort((a,b) => a.order_index - b.order_index);
  const closingItems = profile.checklists.filter(c => c.type === 'closing').sort((a,b) => a.order_index - b.order_index);
  const openingDone = openingItems.filter(c => checkState[c.id]).length;
  const closingDone = closingItems.filter(c => checkState[c.id]).length;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', overflow:'hidden', gap:8 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px 0' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            🤖 {profile.name}
          </div>
          {callerNumber && <div style={{ fontSize:11, color:'#64748b' }}>{callerName || ''} {callerNumber}</div>}
        </div>
        <div style={{ display:'flex', gap:4 }}>
          {profile.actions.map(a => (
            <button key={a.id} onClick={() => handleAction(a)} title={a.label} style={{
              background:'#dbeafe', color:'#2563eb', border:'1px solid #bfdbfe',
              borderRadius:6, padding:'4px 8px', cursor:'pointer', fontSize:11, fontWeight:600, whiteSpace:'nowrap',
            }}>{a.label}</button>
          ))}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:2, padding:'0 10px', borderBottom:'1px solid #e2e8f0' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background:'none', border:'none', cursor:'pointer', padding:'5px 10px', fontSize:12, fontWeight:600,
            color: tab===t.id ? '#3b82f6' : '#64748b',
            borderBottom: tab===t.id ? '2px solid #3b82f6' : '2px solid transparent', marginBottom:-1,
          }}>
            {t.id==='checklist' ? `${t.label} (${openingDone+closingDone}/${openingItems.length+closingItems.length})` : t.label}
          </button>
        ))}
      </div>

      {/* ── CHAT TAB ──────────────────────────────────────────────────────── */}
      {tab === 'chat' && (
        <>
          <div style={{ flex:1, overflowY:'auto', padding:'0 10px', display:'flex', flexDirection:'column', gap:8 }}>
            {messages.length === 0 && (
              <div style={{ padding:'20px 0', textAlign:'center', color:'#64748b', fontSize:12 }}>
                Ask anything about this queue or customer issue...
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role==='user' ? 'flex-end' : 'flex-start',
                maxWidth:'88%', padding:'8px 12px', borderRadius:10, fontSize:12, lineHeight:1.5,
                background: m.role==='user' ? 'linear-gradient(135deg,#3b82f6,#6366f1)' : '#f1f5f9',
                color: '#1e293b',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                cursor: m.role==='assistant' ? 'pointer' : 'default',
              }} title={m.role==='assistant' ? 'Click to copy' : undefined}
                onClick={() => m.role==='assistant' && copyToClipboard(m.content)}>
                {m.content}
              </div>
            ))}
            {thinking && (
              <div style={{ alignSelf:'flex-start', padding:'8px 12px', borderRadius:10, background:'#f1f5f9', fontSize:12, color:'#64748b' }}>
                Thinking...
              </div>
            )}
            <div ref={chatEnd} />
          </div>

          {/* Quick responses */}
          {profile.quick_responses.length > 0 && (
            <div style={{ padding:'0 10px', display:'flex', gap:6, flexWrap:'wrap' }}>
              {profile.quick_responses.slice(0,6).map(qr => (
                <button key={qr.id} onClick={() => sendMessage(qr.response_text)} style={{
                  background:'#ede9fe', color:'#6d28d9', border:'1px solid #c4b5fd',
                  borderRadius:20, padding:'3px 10px', cursor:'pointer', fontSize:11, fontWeight:600,
                }}>{qr.label}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding:'0 10px 10px', display:'flex', gap:8 }}>
            <input style={{ ...INPUT, flex:1 }} placeholder="Ask the AI assistant..." value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if(e.key==='Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
            <button onClick={() => sendMessage()} disabled={thinking || !input.trim()} style={{
              background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'#fff', border:'none',
              borderRadius:6, padding:'8px 14px', cursor:'pointer', fontSize:13, fontWeight:700,
              opacity: (thinking||!input.trim()) ? 0.5 : 1,
            }}>➤</button>
          </div>
        </>
      )}

      {/* ── CHECKLIST TAB ─────────────────────────────────────────────────── */}
      {tab === 'checklist' && (
        <div style={{ flex:1, overflowY:'auto', padding:'0 10px 10px' }}>
          {profile.compliance_notice && (
            <div style={{ padding:10, background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, marginBottom:10, fontSize:12, color:'#92400e', lineHeight:1.5 }}>
              <strong>⚠️ Compliance:</strong> {profile.compliance_notice}
            </div>
          )}
          {openingItems.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                ▶️ Opening Checklist ({openingDone}/{openingItems.length})
              </div>
              {openingItems.map(c => (
                <label key={c.id} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8, cursor:'pointer', padding:'8px 10px', borderRadius:6, background:'#f8fafc', border:'1px solid #e2e8f0' }}>
                  <input type="checkbox" checked={!!checkState[c.id]} onChange={e => setCheckState(s => ({...s,[c.id]:e.target.checked}))}
                    style={{ marginTop:2, accentColor:'#3b82f6', width:15, height:15, flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:12, color:'#1e293b', fontWeight: c.is_required ? 700 : 400 }}>
                      {c.label}{c.is_required && <span style={{ color:'#ef4444', marginLeft:4 }}>*</span>}
                    </div>
                    {c.hint && <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{c.hint}</div>}
                  </div>
                </label>
              ))}
            </div>
          )}
          {closingItems.length > 0 && (
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                🏁 Closing Checklist ({closingDone}/{closingItems.length})
              </div>
              {closingItems.map(c => (
                <label key={c.id} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:8, cursor:'pointer', padding:'8px 10px', borderRadius:6, background:'#f8fafc', border:'1px solid #e2e8f0' }}>
                  <input type="checkbox" checked={!!checkState[c.id]} onChange={e => setCheckState(s => ({...s,[c.id]:e.target.checked}))}
                    style={{ marginTop:2, accentColor:'#3b82f6', width:15, height:15, flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:12, color:'#1e293b', fontWeight: c.is_required ? 700 : 400 }}>
                      {c.label}{c.is_required && <span style={{ color:'#ef4444', marginLeft:4 }}>*</span>}
                    </div>
                    {c.hint && <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{c.hint}</div>}
                  </div>
                </label>
              ))}
            </div>
          )}
          {profile.closing_script && (
            <div style={{ padding:10, background:'#ecfdf5', border:'1px solid #a7f3d0', borderRadius:8, marginTop:10, fontSize:12, color:'#065f46', lineHeight:1.5 }}>
              <strong>Closing Script:</strong> {profile.closing_script}
            </div>
          )}
        </div>
      )}

      {/* ── SUMMARY TAB ───────────────────────────────────────────────────── */}
      {tab === 'summary' && (
        <div style={{ flex:1, overflowY:'auto', padding:'0 10px 10px' }}>
          {!summary ? (
            <div style={{ textAlign:'center', padding:'30px 0' }}>
              <p style={{ fontSize:13, color:'#64748b', marginBottom:16 }}>
                Generate an AI summary of this call to save time on after-call work.
              </p>
              <button onClick={generateSummary} disabled={generatingSummary || messages.length < 2} style={{
                background:'linear-gradient(135deg,#3b82f6,#6366f1)', color:'#fff', border:'none',
                borderRadius:8, padding:'10px 20px', cursor:'pointer', fontSize:14, fontWeight:700,
                opacity: (generatingSummary||messages.length<2) ? 0.5 : 1,
              }}>
                {generatingSummary ? '⏳ Generating...' : '✨ Generate Call Summary'}
              </button>
              {messages.length < 2 && <p style={{ fontSize:11, color:'#64748b', marginTop:8 }}>Use the AI chat first to generate a summary.</p>}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {summary.sentiment && (
                  <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:12, background:'#f1f5f9',
                    color: summary.sentiment==='positive'?'#10b981':summary.sentiment==='negative'?'#ef4444':'#94a3b8' }}>
                    {summary.sentiment}
                  </span>
                )}
                {summary.compliance_ok != null && (
                  <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:12,
                    background: summary.compliance_ok ? '#d1fae5' : '#fee2e2',
                    color: summary.compliance_ok ? '#10b981' : '#ef4444' }}>
                    {summary.compliance_ok ? '✓ Compliant' : '⚠ Compliance Issue'}
                  </span>
                )}
              </div>
              {summary.summary && (
                <div style={{ padding:10, background:'#f8fafc', borderRadius:8, fontSize:12, lineHeight:1.6, color:'#1e293b' }}>
                  {summary.summary}
                </div>
              )}
              {summary.customer_issue && (
                <div><span style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>CUSTOMER ISSUE</span><p style={{ margin:'4px 0 0', fontSize:12, color:'#1e293b' }}>{summary.customer_issue}</p></div>
              )}
              {summary.resolution && (
                <div><span style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>RESOLUTION</span><p style={{ margin:'4px 0 0', fontSize:12, color:'#10b981' }}>{summary.resolution}</p></div>
              )}
              {summary.action_items?.length > 0 && (
                <div>
                  <span style={{ fontSize:11, color:'#64748b', fontWeight:700 }}>ACTION ITEMS</span>
                  <ul style={{ margin:'4px 0 0', paddingLeft:16, fontSize:12, color:'#f59e0b' }}>
                    {(summary.action_items as string[]).map((a,i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {summary.followup_required && (
                <div style={{ padding:8, background:'#fffbeb', borderRadius:6, fontSize:12, color:'#92400e' }}>
                  <strong>Follow-up required:</strong> {summary.followup_notes || 'Yes'}
                </div>
              )}
              <button onClick={() => setSummary(null)} style={{ alignSelf:'center', background:'none', border:'1px solid #e2e8f0', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12, color:'#64748b' }}>
                Regenerate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
